import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';

function History() {
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [swipedId, setSwipedId] = useState(null);
  const [touchStart, setTouchStart] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const teamDoc = await getDoc(doc(db, 'teams', auth.currentUser.uid));
      if (teamDoc.exists()) {
        const voteHistory = teamDoc.data().votes || [];
        setVotes(voteHistory.reverse());
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const editVote = (vote) => {
    navigate('/vote', { state: { editVote: vote } });
  };

  const deleteVote = async (voteId) => {
    if (window.confirm('Delete this round? This cannot be undone.')) {
      try {
        const teamDoc = await getDoc(doc(db, 'teams', auth.currentUser.uid));
        const currentVotes = teamDoc.data().votes || [];
        const updatedVotes = currentVotes.filter(v => v.id !== voteId);

        await updateDoc(doc(db, 'teams', auth.currentUser.uid), {
          votes: updatedVotes
        });

        setVotes(updatedVotes.reverse());
        setSwipedId(null);
      } catch (error) {
        alert('Failed to delete round');
      }
    }
  };

  const onTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const onTouchMove = (e) => {
    const touchCurrent = e.touches[0].clientX;
    const diff = touchStart - touchCurrent;
    
    if (diff > 50) {
      const voteId = e.currentTarget.getAttribute('data-vote-id');
      setSwipedId(voteId);
    } else if (diff < -10) {
      setSwipedId(null);
    }
  };

  const onTouchEnd = (voteId) => {
    // Keep swiped state
  };

  return (
    <div className="container">
      <div className="header">
        <h1>History</h1>
        <button 
          onClick={() => navigate('/')}
          style={{
            background: 'white',
            border: '2px solid white',
            color: '#243665',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '10px 20px',
            borderRadius: '8px',
            fontWeight: '600',
            minWidth: '80px',
            minHeight: '44px',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          Back
        </button>
      </div>

      <div style={{ padding: '20px 0' }}>
        {loading ? (
          <p>Loading...</p>
        ) : votes.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#666' }}>No voting history yet</p>
          </div>
        ) : (
          votes.map(vote => (
            <div key={vote.id} style={{ position: 'relative', marginBottom: '16px' }}>
              <div 
                className="card"
                data-vote-id={vote.id}
                onClick={() => editVote(vote)}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={() => onTouchEnd(vote.id)}
                style={{
                  cursor: 'pointer',
                  transform: swipedId === vote.id ? 'translateX(-90px)' : 'translateX(0)',
                  transition: 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                  position: 'relative',
                  zIndex: 2,
                  touchAction: 'pan-y'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ color: '#243665', marginBottom: '4px' }}>Round {vote.round}</h3>
                    <p style={{ fontSize: '12px', color: '#999' }}>
                      {new Date(vote.date).toLocaleDateString()}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#243665' }}>
                      vs {vote.opponent}
                    </p>
                    {vote.result && (
                      <p style={{ fontSize: '12px', color: '#666' }}>{vote.result}</p>
                    )}
                  </div>
                </div>

                {vote.votes.map((v, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    padding: '8px 0',
                    borderTop: index === 0 ? 'none' : '1px solid #f0f0f0'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: '#243665',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      marginRight: '12px'
                    }}>
                      {v.points}
                    </div>
                    <span style={{ flex: 1, fontSize: '14px', fontWeight: '500' }}>
                      {v.playerName}
                    </span>
                    <span style={{ fontSize: '18px' }}>
                      {v.points === 3 && '🥇'}
                      {v.points === 2 && '🥈'}
                      {v.points === 1 && '🥉'}
                    </span>
                  </div>
                ))}

                <p style={{ 
                  fontSize: '11px', 
                  color: '#999', 
                  marginTop: '12px',
                  textAlign: 'center',
                  borderTop: '1px solid #f0f0f0',
                  paddingTop: '8px'
                }}>
                  Tap to edit • Swipe left to delete
                </p>
              </div>

              {/* Delete button revealed on swipe - iOS style */}
              <div 
                onClick={(e) => {
                  e.stopPropagation();
                  deleteVote(vote.id);
                }}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '8px',
                  bottom: '8px',
                  width: '75px',
                  background: 'linear-gradient(180deg, #ff3b30 0%, #d32f2f 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: '600',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  zIndex: 1,
                  fontSize: '13px',
                  gap: '4px',
                  boxShadow: swipedId === vote.id ? '-2px 0 8px rgba(0,0,0,0.1)' : 'none',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                <div style={{ fontSize: '20px' }}>🗑️</div>
                <div>Delete</div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default History;
